import { useState, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { User, Phone, MapPin, Navigation, IndianRupee } from 'lucide-react';
import { toast } from 'sonner';
import {
    fetchDeliveryChallanDetail,
    updateDeliveryChallanStatus,
    getAllowedNextStatuses,
} from '@/app/services/deliveryChallanService';
import type { DeliveryChallanStatus } from '@/app/types/deliveryChallan';
import { MobileHeader } from '@/components/layout/MobileHeader';
import { MobileCardGroup, MobileCardRow } from '@/components/ui/MobileCard';
import { MobileBadge } from '@/components/ui/MobileBadge';
import { MobileButton } from '@/components/ui/MobileButton';
import { MobileSkeleton } from '@/components/ui/MobileSkeleton';

const statusBadgeVariant: Record<DeliveryChallanStatus, 'default' | 'secondary' | 'success' | 'warning' | 'destructive'> = {
    draft: 'secondary',
    confirmed: 'secondary',
    packed: 'default',
    shipped: 'warning',
    delivered: 'success',
    cancelled: 'destructive',
    completed: 'success',
};

const humanizeStatus = (status: string) =>
    status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export default function DeliveryChallanDetailPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [notes, setNotes] = useState('');

    const { data: detail, isLoading, isError } = useQuery({
        queryKey: ['delivery-challan', id],
        queryFn: () => fetchDeliveryChallanDetail(Number(id)),
        enabled: !!id,
    });

    const mutation = useMutation({
        mutationFn: ({ newStatus, statusNotes }: { newStatus: DeliveryChallanStatus; statusNotes?: string }) =>
            updateDeliveryChallanStatus(Number(id), newStatus, statusNotes),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['delivery-challan', id] });
            queryClient.invalidateQueries({ queryKey: ['delivery-challans'] });
            setNotes('');
            toast.success(`Marked as ${humanizeStatus(variables.newStatus)}.`);
        },
        onError: () => {
            toast.error('Could not update the delivery status. Please try again.');
        },
    });

    const allowedStatuses = useMemo(() => {
        if (!detail) return [] as DeliveryChallanStatus[];
        return getAllowedNextStatuses(detail.status);
    }, [detail]);

    const handleStatusUpdate = (newStatus: DeliveryChallanStatus) => {
        mutation.mutate({
            newStatus,
            statusNotes: notes.trim() || undefined,
        });
    };

    const getButtonVariant = (status: DeliveryChallanStatus): 'primary' | 'destructive' => {
        return status === 'cancelled' ? 'destructive' : 'primary';
    };

    // Loading state
    if (isLoading) {
        return (
            <div className="flex flex-col min-h-full">
                <MobileHeader title="Loading..." showBack />
                <div className="p-4 space-y-4">
                    <MobileSkeleton className="h-32 w-full rounded-xl" />
                    <MobileSkeleton className="h-48 w-full rounded-xl" />
                    <MobileSkeleton className="h-40 w-full rounded-xl" />
                </div>
            </div>
        );
    }

    // Error / not-found state
    if (isError || !detail) {
        return (
            <div className="flex flex-col min-h-full">
                <MobileHeader title="Not Found" showBack />
                <div className="flex-1 flex items-center justify-center p-4">
                    <p className="text-muted-foreground">Delivery challan not found</p>
                </div>
            </div>
        );
    }

    const transactionDate = detail.transaction_date
        ? format(parseISO(detail.transaction_date), 'dd MMM yyyy')
        : '—';

    // Packing is optional and older challans have none — the whole section is
    // hidden rather than showing an empty state the agent has to interpret.
    const packages = detail.packages ?? [];
    const totalPackages =
        detail.total_package_count ?? packages.reduce((sum, pkg) => sum + pkg.quantity, 0);

    return (
        <div className="flex flex-col min-h-full">
            <MobileHeader title="Delivery Details" showBack />

            <div className="flex-1 mobile-scroll-area pb-tab-bar">
                <div className="p-4 space-y-6">
                    {/* Document Info Card */}
                    <div className="mobile-card">
                        <div className="flex items-center justify-between gap-3">
                            <p className="text-xl font-bold text-foreground">
                                {detail.document_number}
                            </p>
                            <MobileBadge variant={statusBadgeVariant[detail.status] ?? 'secondary'}>
                                {humanizeStatus(detail.status)}
                            </MobileBadge>
                        </div>

                        <p className="text-sm text-muted-foreground mt-2">
                            Transaction Date: {transactionDate}
                        </p>

                        {detail.reference_number && (
                            <p className="text-sm text-muted-foreground mt-1">
                                Reference: {detail.reference_number}
                            </p>
                        )}

                        {detail.notes && (
                            <p className="text-sm text-muted-foreground mt-1">
                                Notes: {detail.notes}
                            </p>
                        )}
                    </div>

                    {/* Packing — the physical count to check at load-out and handover.
                        Sits above everything else because "how many pieces am I
                        carrying" is the first thing the agent needs. */}
                    {packages.length > 0 && (
                        <div>
                            <div className="flex items-center justify-between">
                                <h2 className="mobile-section-header">Packing</h2>
                                <span className="text-xs font-semibold text-muted-foreground px-4 py-2 tabular-nums">
                                    {totalPackages} total
                                </span>
                            </div>
                            <MobileCardGroup>
                                {packages.map((pkg) => (
                                    <MobileCardRow key={pkg.id}>
                                        <div className="flex items-center gap-3">
                                            <div className="min-w-[3rem] h-12 px-2 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                                                <span className="text-xl font-bold text-primary tabular-nums">
                                                    {pkg.quantity}
                                                </span>
                                            </div>
                                            <div className="min-w-0">
                                                <p className="font-medium">{pkg.package_type_label}</p>
                                                {pkg.notes && (
                                                    <p className="text-xs text-muted-foreground">{pkg.notes}</p>
                                                )}
                                            </div>
                                        </div>
                                    </MobileCardRow>
                                ))}
                            </MobileCardGroup>
                            <p className="text-xs text-muted-foreground px-4 pt-2">
                                Count every package before you hand over.
                            </p>
                        </div>
                    )}

                    {/* Customer Details */}
                    <div>
                        <h2 className="mobile-section-header">Customer Details</h2>
                        <MobileCardGroup>
                            <MobileCardRow
                                onClick={detail.party_id ? undefined : undefined}
                                showArrow={!!detail.party_id}
                            >
                                {detail.party_id ? (
                                    <Link to={`/parties/${detail.party_id}`} className="flex items-center gap-3 w-full">
                                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                            <User className="h-5 w-5 text-primary" />
                                        </div>
                                        <div>
                                            <p className="text-xs text-muted-foreground">Customer</p>
                                            <p className="font-medium">{detail.party?.name ?? 'Not specified'}</p>
                                        </div>
                                    </Link>
                                ) : (
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                            <User className="h-5 w-5 text-primary" />
                                        </div>
                                        <div>
                                            <p className="text-xs text-muted-foreground">Customer</p>
                                            <p className="font-medium">{detail.party?.name ?? 'Not specified'}</p>
                                        </div>
                                    </div>
                                )}
                            </MobileCardRow>

                            {detail.party?.phone && (
                                <MobileCardRow
                                    onClick={() => { window.location.href = `tel:${detail.party!.phone}`; }}
                                    showArrow
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center flex-shrink-0">
                                            <Phone className="h-5 w-5 text-green-500" />
                                        </div>
                                        <div>
                                            <p className="text-xs text-muted-foreground">Phone</p>
                                            <p className="font-medium">{detail.party.phone}</p>
                                        </div>
                                    </div>
                                </MobileCardRow>
                            )}

                            {detail.party?.shipping_address && (
                                <MobileCardRow>
                                    <div className="flex items-start gap-3">
                                        <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
                                            <MapPin className="h-5 w-5 text-muted-foreground" />
                                        </div>
                                        <div>
                                            <p className="text-xs text-muted-foreground">Shipping Address</p>
                                            <p className="font-medium whitespace-pre-line">{detail.party.shipping_address}</p>
                                        </div>
                                    </div>
                                </MobileCardRow>
                            )}

                            {detail.party?.geo_location && (
                                <MobileCardRow
                                    onClick={() => { window.open(detail.party!.geo_location!, '_blank'); }}
                                    showArrow
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center flex-shrink-0">
                                            <Navigation className="h-5 w-5 text-red-500" />
                                        </div>
                                        <div>
                                            <p className="text-xs text-muted-foreground">Location</p>
                                            <p className="font-medium text-primary">Open in Maps</p>
                                        </div>
                                    </div>
                                </MobileCardRow>
                            )}
                        </MobileCardGroup>

                        {detail.party_id && (
                            <MobileButton
                                variant="primary"
                                className="w-full mt-3"
                                icon={<IndianRupee className="h-5 w-5" />}
                                onClick={() => {
                                    const params = new URLSearchParams({
                                        party_id: String(detail.party_id),
                                    });
                                    if (detail.party?.name) params.set('party_name', detail.party.name);
                                    if (detail.party?.phone) params.set('party_phone', detail.party.phone);
                                    if (detail.site_id) params.set('site_id', String(detail.site_id));
                                    navigate(`/receipts/new?${params.toString()}`);
                                }}
                            >
                                Record Cash Receipt
                            </MobileButton>
                        )}
                    </div>

                    {/* Items */}
                    <div>
                        <h2 className="mobile-section-header">Items</h2>
                        <MobileCardGroup>
                            {detail.items.map((item) => (
                                <MobileCardRow key={item.id}>
                                    <div className="flex items-center justify-between gap-3 w-full">
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium">{item.item.name}</p>
                                            {item.description && (
                                                <p className="text-xs text-muted-foreground">{item.description}</p>
                                            )}
                                        </div>
                                        <p className="text-sm text-muted-foreground whitespace-nowrap">
                                            {item.quantity} {item.unit?.name ?? ''}
                                        </p>
                                    </div>
                                </MobileCardRow>
                            ))}
                        </MobileCardGroup>
                    </div>

                    {/* Status Update Panel */}
                    <div className="mobile-card">
                        <h3 className="text-lg font-semibold">Update Delivery Status</h3>
                        <p className="text-sm text-muted-foreground mt-1">
                            Add optional notes and choose the next status.
                        </p>

                        <textarea
                            className="mobile-input w-full min-h-[80px] resize-none mt-3"
                            placeholder="Add delivery notes (optional)"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            disabled={mutation.isPending}
                        />

                        {allowedStatuses.length === 0 ? (
                            <p className="text-sm text-muted-foreground mt-3">
                                No further actions available for this status.
                            </p>
                        ) : (
                            <div className="flex flex-wrap gap-3 mt-3">
                                {allowedStatuses.map((status) => (
                                    <MobileButton
                                        key={status}
                                        variant={getButtonVariant(status)}
                                        isLoading={mutation.isPending}
                                        disabled={mutation.isPending}
                                        onClick={() => handleStatusUpdate(status)}
                                    >
                                        Mark as {humanizeStatus(status)}
                                    </MobileButton>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
